import {Component, ElementRef, EventEmitter, Input, Output} from "@angular/core";
import {deliveryMethodValues, shippingDestinationValuesBulkOrder} from "../../order/order-value-maps";
import {AddressService} from "../../address/address.service";
import {ContactService} from "../../contact/contact.service";
import {printPreview} from "../../misc/dom-helper";
import {createEvent} from "../../misc/event.polyfill";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import gql from "graphql-tag";
import {GqlMicroFormMutationModelFactory, GqlMicroFormQueryModel} from "../../graphql/graphql.model";
import {OrderFormGqlModel} from "../../order/order-form.gql-model";
import {HardwareOrderDetailsInputModel} from "../../order/order.gql-model";
import {BulkOrderService} from "../../bulk-ordering/bulk-order.service";

@Component({
  selector: "bulk-order-form",
  templateUrl: "./bulk-order-form.template.pug",
  providers: [BulkOrderService, ConfirmDialogService],
})
@exposeComponentInDebugMode()
export class BulkOrderFormComponent {
  private _error: Error;

  shippingDestinationValues = shippingDestinationValuesBulkOrder;
  deliveryMethodValues = deliveryMethodValues;

  editingOrderResults: boolean = false;
  originalOrderResults: any[] = [];

  bulkUpdateOrderNumber: string = "";
  bulkUpdateOrderDate: string = "";

  @Input()
  orderForm: OrderFormGqlModel = new OrderFormGqlModel({});

  @Input()
  allowEditing: {[key: string]: boolean} = {};

  @Output("error")
  errorEmitter: EventEmitter<Event> = new EventEmitter();

  microFormQuery: any = gql`
    ${GqlMicroFormQueryModel.fragment}

    mutation updateBulkOrderFormField($id: ID!, $paramName: String!, $paramValue: JSON!) {
      result: bulkHardwareOrderField(id: $id, paramName: $paramName, paramValue: $paramValue) {
        ...microFormData
      }
    }
  `;

  fieldMutators = [
    "shippingDestination",
    "deliveryMethod",
    "salValNumber",
    "shipCompleted",
    "estimatedDeliveryDate",
    "estimatedHardwareOrderDate",
    "targetDeliveryDate",
    "requestedDeliveryDate",
    "extraPricingInformation",
    "resellerInformation",
    "resellerInstructions",
    "deliveryInformation",
    "orderNotificationEmailList",
    "sourceDocumentationLink",
  ];

  microFormMutators: {[key: string]: GqlMicroFormMutationModelFactory} = this.fieldMutators.reduce(
    (obj, key) => {
      obj[key] = new GqlMicroFormMutationModelFactory(key, () => this.orderForm.id, this.microFormQuery, "result");
      return obj;
    },
    {},
  );

  set error(error: Error) {
    this._error = error;
    const event = createEvent("error");
    event["error"] = error;
    this.errorEmitter.emit(event);
  }

  get error(): Error {
    return this._error;
  }

  constructor(
    private bulkOrderService: BulkOrderService,
    private confirmDialogService: ConfirmDialogService,
    private addressService: AddressService,
    private contactService: ContactService,
    private elementRef: ElementRef,
  ) {}

  forwardError(event): void {
    this.errorEmitter.emit(event);
  }

  @handleErrors()
  async printPreview() {
    const element = this.elementRef.nativeElement;
    const sourceElement = element.querySelector("bulk-order-form > div.row > div.col") as HTMLDivElement;

    await printPreview(sourceElement, "Print Bulk Order Form");
  }

  @handleErrors()
  async editInstallationContact() {
    const {changed, contact} = await this.contactService.modifyContact("installation", "bulkOrder", this.orderForm.id);

    if (changed) {
      this.orderForm.header.installationContact = contact;
    }
  }

  @handleErrors()
  async editDeliveryContact() {
    const {changed, contact} = await this.contactService.modifyContact("delivery", "bulkOrder", this.orderForm.id);

    if (changed) {
      this.orderForm.header.deliveryContact = contact;
    }
  }

  @handleErrors()
  async editConfigurationAddress() {
    const {changed, address} = await this.addressService.modifyAddress(
      "bulk_order_configuration_address",
      this.orderForm.id,
    );

    if (changed) {
      this.orderForm.header.configurationAddress = address;
    }
  }

  @handleErrors()
  async editShipToAddress() {
    const {changed, address} = await this.addressService.modifyAddress(
      "bulk_order_ship_to_address",
      this.orderForm.id,
    );

    if (changed) {
      this.orderForm.header.shipToAddress = address;
    }
  }

  editOrderResults() {
    this.originalOrderResults = this.orderForm.devices.map(({orderNumber, orderDate}) => ({orderNumber, orderDate}));
    this.bulkUpdateOrderNumber = "";
    this.bulkUpdateOrderDate = "";
    this.editingOrderResults = true;
  }

  copyBulkOrderNumberAndDate() {
    if (this.bulkUpdateOrderNumber) {
      this.orderForm.devices.forEach((it) => { it.orderNumber = this.bulkUpdateOrderNumber; });
    }

    if (this.bulkUpdateOrderDate) {
      this.orderForm.devices.forEach((it) => { it.orderDate = this.bulkUpdateOrderDate; });
    }
  }

  cancelOrderResults() {
    this.orderForm.devices.forEach((it, index) => {
      if (!this.originalOrderResults[index]) return;
      it.orderNumber = this.originalOrderResults[index].orderNumber;
      it.orderDate = this.originalOrderResults[index].orderDate;
    });

    this.editingOrderResults = false;
  }

  @handleErrors()
  async saveOrderResults() {
    const updates = this.orderForm.devices
      .filter(it => !it.indented && !it.multipleOrderNumbers)
      .map(it => new HardwareOrderDetailsInputModel(it));

    await this.bulkOrderService.updateOrderNumbersAndDates(this.orderForm.id, updates);

    this.editingOrderResults = false;
  }
}
